import ApiResponse from "@core/types/apiResponse";
import { ReportTemplateResponse, OriginScope } from "@features/report/template/types/reportTemplate";
import { CreateTemplateRequest, UpdateTemplateRequest } from "@features/report/template/types/reportTemplateRequests";
import { fetchTemplate, fetchTemplatePreview, createTemplate, updateTemplate, assignTemplateActivities, removeTemplateActivities, enableTemplate, disableTemplate, fetchAssignedTemplateForChruchAsync as fetchAssignedTemplateForChurchFromApi, fetchAssignedTemplateForDistrictAsync as fetchAssignedTemplateForDistrictFromApi, fetchAssignedTemplateForNationAsync as fetchAssignedTemplateForNationFromApi, changeTemplateOriginScope } from "@features/report/template/api/reportTemplateApi";
import { ReportTemplatePreviewResponse } from "@features/report/template/types/reportTemplatePreview";

/**
 * Fetches a report template for a given department using the provided parameters.
 *
 * This async wrapper forwards the department identifier and params to the underlying
 * `fetchTemplate` implementation and returns its ApiResponse containing a
 * ReportTemplateResponse.
 *
 * @async
 * @param departmentId - The numeric ID of the department to fetch the template for.
 * @param params - Additional query or request parameters (structure depends on backend API).
 * @returns A Promise that resolves to an ApiResponse<ReportTemplateResponse>.
 * @throws Propagates any errors thrown by the underlying `fetchTemplate` call (e.g., network errors or response parsing errors).
 */
export const fetchTemplateAsync = async (departmentId: number, params: any): Promise<ApiResponse<ReportTemplateResponse>> => {
    const response = await fetchTemplate(departmentId, params);
    return response;
};

export const fetchAssignedTemplateForChruchAsync = async (churchId: number, departmentId: number, params: any): Promise<ApiResponse<ReportTemplateResponse>> => {
    const response = await fetchAssignedTemplateForChurchFromApi(churchId, departmentId, params);
    return response;
};

export const fetchAssignedTemplateForDistrictAsync = async (districtId: number, departmentId: number, params: any): Promise<ApiResponse<ReportTemplateResponse>> => {
    const response = await fetchAssignedTemplateForDistrictFromApi(districtId, departmentId, params);
    return response;
};

export const fetchAssignedTemplateForNationAsync = async (nationId: number, departmentId: number, params: any): Promise<ApiResponse<ReportTemplateResponse>> => {
    const response = await fetchAssignedTemplateForNationFromApi(nationId, departmentId, params);
    return response;
};

export const fetchTemplatePreviewAsync = async (
    departmentId: number,
    reportTemplateId: number,
    scope?: { nationId?: number; districtId?: number; churchId?: number }
): Promise<ApiResponse<ReportTemplatePreviewResponse>> => {
    const response = await fetchTemplatePreview(departmentId, reportTemplateId, scope);
    return response;
};

/**
 * Creates a new report template for a specific nation and department context by delegating to the underlying API call.
 *
 * @param nationId - Numeric identifier of the nation under which the template will be created.
 * @param departmentId - Numeric identifier of the department that will own the template.
 * @param data - The template payload to create (CreateTemplateRequest).
 * @returns A Promise that resolves to an ApiResponse with the server result.
 * @throws Propagates any error thrown by the underlying createTemplate call (e.g., network, authorization, or validation errors).
 * @remarks The function forwards the request to createTemplate and returns its response unchanged.
 * 
 */
export const createTemplateAsync = async (nationId: number, departmentId: number, data: CreateTemplateRequest): Promise<ApiResponse<any>> => {
    const response = await createTemplate(nationId, departmentId, data);
    return response;
};

/**
 * Asynchronously updates a template within a given nation and department context.
 *
 * Sends an update request for the template identified by `templateId` under the specified
 * `nationId` and `departmentId`, using the provided `data` payload.
 *
 * @param nationId - The numeric identifier for the nation context.
 * @param departmentId - The numeric identifier for the department.
 * @param templateId - The numeric identifier of the template to update.
 * @param data - The update payload (UpdateTemplateRequest) containing the new template fields.
 * @returns A Promise that resolves to an ApiResponse containing the result of the update operation.
 * @throws Will reject the promise if the underlying update request fails (e.g., network or validation errors).
 * 
 */
export const updateTemplateAsync = async (nationId: number, departmentId: number, templateId: number, data: UpdateTemplateRequest): Promise<ApiResponse<any>> => {
    const response = await updateTemplate(nationId, departmentId, templateId, data);
    return response;
};

/**
 * Assigns activities from a template to a specific department for a given nation user.
 *
 * This async wrapper forwards the provided identifiers and payload to the
 * underlying `assignTemplateActivities` service call and returns its ApiResponse.
 *
 * @param nationId - The nation identifier of the user performing the assignment.
 * @param departmentId - The identifier of the department receiving the template activities.
 * @param templateId - The identifier of the template containing activities to assign.
 * @param data - Payload containing activity assignment details (shape depends on the backend API).
 * @returns A promise that resolves to an ApiResponse<any> representing the result of the assignment operation.
 * @throws Will reject if the underlying API call fails (network error, server error, validation failure, etc.).
 *
 */
export const assignTemplateActivitiesAsync = async (nationId: number, departmentId: number, templateId: number, data: any): Promise<ApiResponse<any>> => {
    const response = await assignTemplateActivities(nationId, departmentId, templateId, data);
    return response;
};

export const removeTemplateActivitiesAsync = async (
    nationId: number,
    departmentId: number,
    templateId: number,
    data: { activity_ids: number[] }
): Promise<ApiResponse<any>> => {
    const response = await removeTemplateActivities(nationId, departmentId, templateId, data);
    return response;
};

/** Disables a report template (sets it inactive). */
export const disableTemplateAsync = async (nationId: number, departmentId: number, templateId: number): Promise<ApiResponse<any>> => {
    return disableTemplate(nationId, departmentId, templateId);
};

/** Enables a report template (sets it active). */
export const enableTemplateAsync = async (nationId: number, departmentId: number, templateId: number): Promise<ApiResponse<any>> => {
    return enableTemplate(nationId, departmentId, templateId);
};

/** Changes origin scope (who completes this template). */
export const changeTemplateOriginScopeAsync = async (
    nationId: number,
    departmentId: number,
    templateId: number,
    originScope: OriginScope
): Promise<ApiResponse<any>> => {
    return changeTemplateOriginScope(nationId, departmentId, templateId, { new_origin_scope: originScope });
};

